import classNames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { StatusBanner } from '@xingternal/banner';
import { trackEvent } from '@xingternal/hops-tracking';
import { Headline } from '@xingternal/typography';
import { Column, Row } from '@xingternal/xing-grid';

import { JobPostingModule } from '@/components/job-posting-module';
import { JDPModuleName } from '@/components/job-posting/types';
import { JobDetailContext } from '@/contexts';

import { ScrollTrackingModuleWrapper } from '../scroll-tracking-module-wrapper';
import Player from './components/player';

import styles from './company-video.css';

type CompanyVideo = {
  moduleName: JDPModuleName;
  video: {
    url: string;
  };
};

type VideoProps = {
  url: string;
  onReady: () => void;
  onError: () => void;
  onStart: () => void;
  onEnded: () => void;
};

const VideoError: React.FC = () => (
  <div className={styles.statusBannerContainer}>
    <StatusBanner display="inline" variant="error">
      <FormattedMessage id="JOBS_DEFAULT_ERROR_MESSAGE" />
    </StatusBanner>
  </div>
);

const Video: React.FC<VideoProps> = ({ url, onReady, onError, onStart, onEnded }) => (
  <Player
    render={({ Component, loading, error }): JSX.Element | null => {
      if (loading) return null;
      if (error) return <VideoError />;
      return (
        <Component
          url={url}
          className={styles.reactPlayer}
          width="100%"
          height="100%"
          controls
          onReady={onReady}
          onError={onError}
          onStart={onStart}
          onEnded={onEnded}
        />
      );
    }}
  />
);

export const CompanyVideo: React.FC<CompanyVideo> = ({ moduleName, video }) => {
  const { id: jobId } = React.useContext(JobDetailContext);
  const [hasError, setError] = React.useState(false);
  const [isReady, setReady] = React.useState(false);

  const onReady = (): void => {
    setReady(true);
    trackEvent('EventVideoLoaded', { EventVideoLoaded: 1, PropJobsId: jobId });
  };
  const onError = (): void => setError(true);
  const onStart = (): void =>
    trackEvent('EventVideoStarted', {
      EventVideoStarted: 1,
      PropActionOrigin: `video_autoplay_no`,
      PropJobsId: jobId,
    });
  const onEnded = (): void =>
    trackEvent('EventVideoFinished', {
      EventVideoFinished: 1,
      PropJobsId: jobId,
    });

  return (
    <ScrollTrackingModuleWrapper moduleTrackingName={moduleName}>
      <JobPostingModule>
        <Column size={12}>
          <Row>
            <Column size={12} sizeConfined={4}>
              <Headline size="medium" sizeWide="large" className={styles.videoTitle} as="h3">
                <FormattedMessage id="JOBS_DETAIL_EMPLOYER_SHOWCASE_VIDEO" />
              </Headline>
            </Column>
            <Column size={12} sizeConfined={8}>
              <div className={classNames(styles.playerWrapper, { [styles.playerReady]: isReady })}>
                {hasError ? <VideoError /> : <Video url={video.url} {...{ onReady, onError, onStart, onEnded }} />}
              </div>
            </Column>
          </Row>
        </Column>
      </JobPostingModule>
    </ScrollTrackingModuleWrapper>
  );
};
